"use client";

import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import Image from "next/image";
import { Breadcrumb } from "../breadcrumb";
import { Heading1 } from "../typography";
import { ButtonLink } from "../button-link";
import { BannerBlob, BannerStats, type BannerStat } from "../shared";

const bannerStats: BannerStat[] = [
  { value: "100%", label: "National Coverage", color: "blue" },
  { value: "Free", label: "C+B On-Demand Assessments", color: "green" },
  { value: "1,000+", label: "Monthly Users", color: "orange" },
];

export const Banner = () => {
  useSectionNavTheme({
    trigger: "#planr-banner",
    isDark: false,
  });

  useGSAP(() => {
    const tl = gsap.timeline();

    const splitTitle = new SplitText(".banner-text", {
      type: "words,chars",
    });

    tl.from(splitTitle.chars, {
      opacity: 0,
      y: 20,
      duration: 0.8,
      stagger: 0.02,
      ease: "power2.out",
    }).from(
      ".breadcrumb",
      {
        opacity: 0,
        y: 20,
        duration: 0.5,
        ease: "power2.out",
      },
      "-=0.3"
    );
  });

  return (
    <section
      id="planr-banner"
      className="relative py-20 lg:py-32 bg-haizea-dark min-h-screen flex flex-col overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <BannerBlob className="z-0 opacity-30" />
        <Image
          src="/images/dam-water.webp"
          alt="" // Decorative background
          fill
          className="absolute z-10 inset-0 object-cover w-full h-full opacity-40 dark:opacity-30"
          style={{ objectPosition: "center" }}
          priority
          fetchPriority="high"
          draggable={false}
          data-speed="0.5"
        />
      </div>
      <div className="container relative mb-16">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Case Studies", href: "/case-studies" },
            { label: "PLANR Platform" },
          ]}
          className="mb-8 absolute left-0 top-0"
        />
      </div>
      <div className="container relative z-10 flex-1 flex items-center justify-center">
        <div className="max-w-5xl mx-auto text-center">
          <Heading1 className="banner-text text-white">
            Enabling Australia&apos;s Nature Repair Market
          </Heading1>

          {/* Stats Grid */}
          <BannerStats
            stats={bannerStats}
            className="mt-8 lg:mt-12 max-w-4xl justify-center mx-auto"
          />

          <div
            id="banner-buttons"
            className="flex items-center justify-center gap-4 md:gap-8 mt-8 lg:mt-12 lg:gap-10 flex-wrap"
          >
            <ButtonLink
              backgroundColorClass="bg-haizea-green"
              href={"https://planr.gov.au/"}
              target="_blank"
            >
              Visit PLANR website
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>
  );
};
